// ─── Naming mask utilities ────────────────────────────────────────────────────
// Supports Python-like format: {var}, {var:02d}, {var:03d}

export type PathwayNodeLabel = { id?: string; name?: unknown }
export type PathwayEdgeLabel = PathwayNodeLabel & { label?: unknown; source?: unknown; target?: unknown; from?: unknown; to?: unknown; mediaType?: unknown; type?: unknown }
export function pathwayNodeName(node?: PathwayNodeLabel): string {
  return typeof node?.name === 'string' && node.name.trim() ? node.name.trim() : node?.id ? `Узел ${node.id}` : 'Узел не задан'
}
export function pathwayEdgeName(edge: PathwayEdgeLabel, nodes: PathwayNodeLabel[] = []): string {
  if (typeof edge.name === 'string' && edge.name.trim()) return edge.name.trim()
  if (edge.name === undefined && typeof edge.label === 'string' && edge.label.trim()) return edge.label.trim()
  const source = edge.source ?? edge.from, target = edge.target ?? edge.to
  if (source == null && target == null) return `Магистрали ${edge.id}`
  const endpoint = (id: unknown) => pathwayNodeName(nodes.find(node => String(node.id) === String(id)) || (id == null ? undefined : { id: String(id) }))
  const media = edge.mediaType ?? edge.type
  return `${media === 'fiber' ? 'Оптика' : media === 'copper' ? 'Медь' : media === 'power' ? 'Питание' : 'Среда не задана'} · ${endpoint(source)} → ${endpoint(target)}`
}

/**
 * Apply a naming mask to a set of variables.
 * Example: applyMask("{module}/{row:02d}/{rack:02d}", {module:"1", row:5, rack:13}) → "1/05/13"
 */
export function applyMask(mask: string, vars: Record<string, string | number>): string {
  return mask.replace(/\{([^}:]+)(?::0?(\d+)d)?\}/g, (_, key: string, width?: string) => {
    const val = vars[key];
    if (val === undefined || val === null) return `{${key}}`;
    const str = String(typeof val === 'number' ? Math.round(val) : val);
    if (width) return str.padStart(parseInt(width), '0');
    return str;
  });
}

/** Convert 0-based row index to letter(s): 0→A, 25→Z, 26→AA, 27→AB */
export function rowIndexToLetter(index: number): string {
  let result = '';
  let n = index;
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

/** Build all mask variables for a given row/rack position */
export function buildMaskVars(opts: {
  module: string;
  hall: string;
  row: number;      // 1-based
  rack: number;     // 1-based
  custom?: Record<string, string>;
}): Record<string, string | number> {
  return {
    module: opts.module,
    // Backend masks historically use both {module} and {prefix} for the
    // same operator-facing value. Keep both aliases in the shared path.
    prefix: opts.module,
    hall: opts.hall,
    row: opts.row,
    rack: opts.rack,
    row_letter: rowIndexToLetter(opts.row - 1),
    ...( opts.custom ?? {}),
  };
}

/** Validate that a mask contains at least {rack} */
export function validateMask(mask: string): { ok: boolean; error?: string } {
  if (!mask.trim()) return { ok: false, error: 'Маска не может быть пустой' };
  if (!mask.includes('{row')) return { ok: false, error: 'Маска должна содержать {row}, {row_letter} или их формат' };
  if (!mask.includes('{rack')) return { ok: false, error: 'Маска должна содержать {rack} или {rack:02d}' };
  return { ok: true };
}

/** Preview mask with example values */
export function previewMask(mask: string, module: string, hall: string): string {
  const vars = buildMaskVars({ module, hall, row: 5, rack: 13 });
  try {
    return applyMask(mask, vars);
  } catch {
    return '(ошибка)';
  }
}

/** Preset naming masks */
export const MASK_PRESETS: { label: string; mask: string; example: string }[] = [
  {
    label: 'Ряд/Место (A-01)',
    mask: '{row_letter}-{rack:02d}',
    example: 'A-01, B-13',
  },
  {
    label: 'Модуль/Ряд/Место (1/05/13)',
    mask: '{module}/{row:02d}/{rack:02d}',
    example: '1/05/13, 1/05/14',
  },
  {
    label: 'Зал+Ряд+Место (A01-13)',
    mask: '{hall}{row:02d}-{rack:02d}',
    example: 'A05-13, A05-14',
  },
  {
    label: 'Числовой (1-001)',
    mask: '{row}-{rack:03d}',
    example: '5-013, 5-014',
  },
];
